import type { RefObject } from 'react';
import { useEffect } from 'react';

import styles from './videos.module.css';

/**
 * Rota "sem × com" entre o texto e os vídeos (motion do bloco; só nos pares da lista de src/components/motion/ativacao.ts,
 * e este arquivo é leve: só o JSX e a medição, sem GSAP — a animação está em motion.ts, por import()): do texto desce um tronco que se
 * divide em dois ramos. O da esquerda chega ao vídeo "Sem NoPing" serrilhado e vermelho (conexão instável); o da
 * direita chega liso e verde ao "Com NoPing". A geometria sai das caixas reais (cabeçalho, cartões) e é refeita quando
 * o bloco muda de tamanho; os traços usam pathLength=1, então a cena (motion.ts) desenha por stroke-dashoffset 1 → 0 sem
 * depender do comprimento em px. Sem a cena (Off, reduced-motion, sem JS de motion) a rota fica inteira, parada.
 */
export function RotaVideos() {
  return (
    <svg className={styles.rota} data-vcs-rota='' aria-hidden='true' focusable='false'>
      <path className={styles.rotaTrilho} data-r='trilho' />
      <path className={styles.rotaTronco} data-r='tronco' pathLength={1} />
      <path className={styles.rotaSem} data-r='sem' pathLength={1} />
      <path className={styles.rotaCom} data-r='com' pathLength={1} />
      <circle className={styles.rotaNo} data-r='no-centro' r={3.5} />
      <circle className={`${styles.rotaNo} ${styles.rotaNoSem}`} data-r='no-sem' r={3.5} />
      <circle className={`${styles.rotaNo} ${styles.rotaNoCom}`} data-r='no-com' r={3.5} />
      <circle className={styles.pacoteSem} data-r='pacote-sem' r={2.6} />
      <circle className={styles.pacoteCom} data-r='pacote-com' r={2.6} />
    </svg>
  );
}

const JAG = [0, -3.2, 2.6, -1.4, 3.6, -2.4, 1.2, -3.8, 2.2, -1.8, 3.1, -2.9, 1.6, -3.4, 2.8, -1.1];

const f1 = (value: number) => Math.round(value * 10) / 10;

/** Ramo serrilhado (sem NoPing): dente a cada ~13 px, amplitude fixa (determinística), curva no fim para descer. */
function ramoSerrilhado(x0: number, y: number, x1: number, yEnd: number, r: number): string {
  const dir = x1 < x0 ? -1 : 1;
  const run = Math.abs(x1 - x0) - r;
  const steps = Math.max(4, Math.round(run / 13));
  let d = `M${f1(x0)} ${f1(y)}`;
  for (let i = 1; i < steps; i += 1) d += `L${f1(x0 + (dir * run * i) / steps)} ${f1(y + (JAG[i % JAG.length] ?? 0))}`;
  d += `L${f1(x1 - dir * r)} ${f1(y)}Q${f1(x1)} ${f1(y)} ${f1(x1)} ${f1(y + r)}V${f1(yEnd)}`;
  return d;
}

const ramoLiso = (x0: number, y: number, x1: number, yEnd: number, r: number) => {
  const dir = x1 < x0 ? -1 : 1;
  return `M${f1(x0)} ${f1(y)}H${f1(x1 - dir * r)}Q${f1(x1)} ${f1(y)} ${f1(x1)} ${f1(y + r)}V${f1(yEnd)}`;
};

const setAttrs = (el: Element | null, attrs: Record<string, string | number>) => {
  if (!el) return;
  Object.entries(attrs).forEach(([name, value]) => el.setAttribute(name, String(value)));
};

/** Caixa do elemento no sistema do container (offsets: não muda com os transforms da chegada). */
function caixa(el: HTMLElement, inner: HTMLElement) {
  let left = 0;
  let top = 0;
  let node: HTMLElement | null = el;
  while (node && node !== inner) {
    left += node.offsetLeft;
    top += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return { left, top, width: el.offsetWidth, height: el.offsetHeight };
}

/** Vão mínimo (px) entre o cabeçalho e os vídeos para a rota caber (tronco + curva + nós) sem invadir os cartões. */
const VAO_MINIMO = 18;

/**
 * Mede o vão entre o cabeçalho e os vídeos e desenha a rota nele. Tudo sai das caixas reais (offsets): título em 1 ou 5
 * linhas, CJK, índicas, textos longos ou telas baixas (margem do cabeçalho menor) só mudam o vão medido. Se o vão não
 * comporta a rota (ou ainda não há layout), ela some — nunca fica solta nem por cima dos vídeos.
 */
function desenhar(inner: HTMLElement) {
  const svg = inner.querySelector<SVGSVGElement>('[data-vcs-rota]');
  const head = inner.querySelector<HTMLElement>('header');
  const cards = Array.from(inner.querySelectorAll<HTMLElement>('[data-vcs] > figure'));
  const [sem, com] = cards;
  if (!svg || !head || !sem || !com) return;
  const headBox = caixa(head, inner);
  const semBox = caixa(sem, inner);
  const comBox = caixa(com, inner);
  const lado = Math.abs(semBox.top - comBox.top) < 4; // vídeos lado a lado (computador) ou empilhados (celular)
  const top = headBox.top + headBox.height + 6;
  const h = semBox.top - top;
  const w = inner.clientWidth;
  if (!(h >= VAO_MINIMO) || !(w > 0)) {
    svg.removeAttribute('data-medida');
    return;
  }
  svg.style.top = `${f1(top)}px`;
  svg.style.height = `${f1(h)}px`;
  svg.setAttribute('viewBox', `0 0 ${f1(w)} ${f1(h)}`);
  const cx = w / 2;
  const split = h * 0.4;
  const xSem = lado ? semBox.left + semBox.width / 2 : w * 0.24;
  const xCom = lado ? comBox.left + comBox.width / 2 : w * 0.76;
  const r = Math.min(10, (h - split) * 0.6);
  const tronco = `M${f1(cx)} 0V${f1(split)}`;
  const dSem = ramoSerrilhado(cx, split, xSem, h, r);
  const dCom = ramoLiso(cx, split, xCom, h, r);
  setAttrs(svg.querySelector('[data-r="trilho"]'), { d: `${tronco}${ramoLiso(cx, split, xSem, h, r)}${ramoLiso(cx, split, xCom, h, r)}` });
  setAttrs(svg.querySelector('[data-r="tronco"]'), { d: tronco });
  setAttrs(svg.querySelector('[data-r="sem"]'), { d: dSem });
  setAttrs(svg.querySelector('[data-r="com"]'), { d: dCom });
  setAttrs(svg.querySelector('[data-r="no-centro"]'), { cx: f1(cx), cy: f1(split) });
  setAttrs(svg.querySelector('[data-r="no-sem"]'), { cx: f1(xSem), cy: f1(h - 1) });
  setAttrs(svg.querySelector('[data-r="no-com"]'), { cx: f1(xCom), cy: f1(h - 1) });
  // só agora a rota tem geometria: até aqui (HTML do servidor, sem JS, antes de hidratar) o CSS a deixa escondida
  svg.setAttribute('data-medida', '');
}

/**
 * Desenha a rota já no 1º efeito (vale também em Off) e de novo a cada mudança de tamanho do bloco. `enabled` = motion
 * ligado no par (e o bloco com texto): fora dele o efeito não faz nada (e o SVG nem existe no HTML).
 */
export function useRotaVideos(sectionRef: RefObject<HTMLElement | null>, enabled: boolean): void {
  useEffect(() => {
    const inner = sectionRef.current?.querySelector('[data-vcs-rota]')?.parentElement;
    if (!enabled || !inner) return undefined;
    desenhar(inner);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => desenhar(inner));
    observer.observe(inner);
    return () => observer.disconnect();
  }, [sectionRef, enabled]);
}
